import DateTime from 'utils/datetime';
import { PackageListQueryParams } from './hooks/usePackagesQuery';
import type { PackageDTO, PackageListDTO } from './types';

const STUCK_THRESHOLD_DAYS = 2; // Configurable threshold

export const findAllStuckPackages = (packages: PackageListDTO) =>
  packages?.reduce(
    (acc, curr) => {
      if (curr?.invoicePrimaryId) return acc;

      const { isStuck, daysSinceLatestStep } = isPackageStuck(curr) || {};

      if (isStuck) {
        acc[curr.packageNumber] = daysSinceLatestStep;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

export const isPackageStuck = (pkg: PackageDTO) => {
  const daysSinceLatestStep =
    pkg?.invoicePrimaryId || !pkg?.lastModifiedDate
      ? 0
      : DateTime.diff(DateTime.now(), pkg?.lastModifiedDate!, 'day');

  return {
    isStuck: daysSinceLatestStep >= STUCK_THRESHOLD_DAYS,
    daysSinceLatestStep,
  };
};

export const getStatusAge = (pkg: PackageDTO) => {
  if (pkg?.invoicePrimaryId) return null;

  const now = DateTime.now();
  if (pkg?.shipmentDeliveredDate) {
    return DateTime.diff(now, pkg?.shipmentDeliveredDate!, 'days');
  }

  if (pkg?.packageDate) {
    return DateTime.diff(now, pkg?.packageDate, 'days');
  }

  return null;
};

export const constructPackageListQueryParams = (
  page: number,
  size: number,
  agronomist: string[],
  packageStatus: string[],
  shipmentStatus: string[],
  soNumber: string | null,
  farmerGroup: string[],
  isViewingStuckPackages: boolean,
  isViewingPackagesWithoutInvoice: boolean,
  dateRange: [string, string] | null,
) => {
  const params: PackageListQueryParams = {
    page,
    size,
  } as PackageListQueryParams;

  const hasFulfilled = packageStatus.includes('fulfilled');

  params['soNumber.contains'] = soNumber as string;

  if (shipmentStatus?.length > 0)
    params['salesStatus.in'] = shipmentStatus.join(',');

  if (farmerGroup?.length > 0)
    params['farmerGroupId.in'] = farmerGroup.join(',');

  if (agronomist?.length > 0) params['agId.in'] = agronomist.join(',');

  let filtered: string[] = packageStatus;

  if (packageStatus?.length > 0) {
    if (hasFulfilled) {
      filtered = packageStatus.filter((status) => status !== 'fulfilled');
      params['invoicePrimaryId.specified'] = true;
    }
    params['packageStatus.in'] = filtered?.join(',');
  }

  if (filtered?.length === 0) {
    params['packageStatus.in'] = 'shipped,delivered';
  }

  if (isViewingStuckPackages) {
    params['lastModifiedDate.lessThan'] = DateTime.subtract(
      DateTime.now(),
      STUCK_THRESHOLD_DAYS,
      'day',
    ).toISOString();
    params['packageStatus.in'] = 'shipped,delivered';
    params['invoicePrimaryId.specified'] = false;
  }

  if (dateRange?.[0]) {
    params['packageCreatedDate.greaterThanOrEqual'] = dateRange[0];
  }

  if (dateRange?.[1]) {
    params['packageCreatedDate.lessThanOrEqual'] = dateRange[1];
  }

  if (
    (isViewingPackagesWithoutInvoice && !hasFulfilled) ||
    (filtered?.length > 0 && !hasFulfilled)
  )
    params['invoicePrimaryId.specified'] = false;

  return params;
};
